import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, TemplateRef, ViewChild, AfterViewInit } from "@angular/core";
import { MatTableModule } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatIcon } from "@angular/material/icon";
import { MatTableDataSource } from "@angular/material/table";
import { MatDialog } from "@angular/material/dialog";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { Observable, merge, of as observableOf, Subject } from "rxjs";
import { catchError, map, startWith, switchMap, filter, delay } from "rxjs/operators";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from "@angular/material/tabs";
import { SocialAIContentSIteI } from "../../../../interfaces/core.interface"; 
import { CoreService } from "../../../../services/core.service"; 
import { DialogComponent } from "../../../dialog/dialog.component";
import { SocialAiSiteFormComponent } from "../../../forms/create-account/site-form/site-form.component";

@Component({
  selector: 'app-site-table',
  standalone: true, 
  imports: [CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginator,
    MatIcon,
    MatToolbarModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    MatTabsModule,
    SocialAiSiteFormComponent],
  templateUrl: './site-table.component.html',
  styleUrl: './site-table.component.scss'
})
export class SocialAiAdminSiteTableComponent implements AfterViewInit, OnInit {

  private _companyID: string | null = null;
  private companyIdStream$ = new Subject<void>();

  @Input()
  set companyID(id: string | null) {
    if (this._companyID !== id) {
      this._companyID = id;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.companyIdStream$.next();
    }
  }

  get companyID(): string | null {
    return this._companyID;
  }

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("siteFormTmpl", { static: true }) siteFormTmpl!: TemplateRef<any>;
  @ViewChild("confirmDeleteSiteTmpl", { static: true })
  confirmDeleteSiteTmpl!: TemplateRef<any>;

  private AllowNewDomain: boolean = true;

  selectedSite: SocialAIContentSIteI = {} as SocialAIContentSIteI;
  dataSource = new MatTableDataSource<SocialAIContentSIteI>();
  displayedColumns: string[] = [
    "ID",
    "Domain",
    "siteUrl",
    "isApproved",
    "actions",
  ];

  showFilters: boolean = false;
  totalItems: number = 0;

  constructor(private coreService: CoreService, public dialog: MatDialog) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const dataLoadTrigger$ = merge(
      this.sort.sortChange,
      this.paginator.page,
      this.companyIdStream$.asObservable()
    ).pipe(
      map(() => ({})), 
      startWith({}), 
      delay(0), 
      filter(() => this._companyID !== null) 
    );

    dataLoadTrigger$.pipe(
      switchMap(() => this.getSitesDataObservable()),
      catchError(() => observableOf({ data: [], total: 0 }))
    ).subscribe((data: { data: SocialAIContentSIteI[], total: number }) => { 
      this.dataSource.data = data.data;
      this.totalItems = data.total;
    });
  }

  /**
   * @returns 
   */
  private getSitesDataObservable(): Observable<{ data: SocialAIContentSIteI[], total: number }> {
    const params: any = {
      sortBy: this.sort.active ?? "",
      sortOrder: this.sort.direction ?? "",
      offset: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
    };

    if (this.companyID) {
      params.companyID = Number(this.companyID);
    } else {
      console.warn("getSitesDataObservable called without companyID in params. This should not happen.");
      return observableOf({ data: [], total: 0 });
    }

    return this.coreService
      .getData("/getSitesPerCompany", params)
      .pipe(
        catchError((error) => {
          console.error("Error fetching sites:", error);
          return observableOf({ data: [], total: 0 });
        }),
        map((response: { data: SocialAIContentSIteI[], total: number }) => { 
          if (!response || !response.data) {
            return { data: [], total: 0 };
          }

          const sitesToDisplay: SocialAIContentSIteI[] = [];

          response.data.forEach((site: SocialAIContentSIteI) => {
            if (this.companyID && site.companyID !== Number(this.companyID)) {
                return;
            }
            sitesToDisplay.push(site);
          });

          return { data: sitesToDisplay, total: response.total };
        })
      );
  }

  loadSites(): void {
    if (this._companyID === null) {
      this.coreService.showSnackbar("Please select a company before loading sites.");
      return;
    }
    this.paginator.pageIndex = 0;
    this.companyIdStream$.next();
  }

  addNewSite(): void {
    if (this.companyID === null) {
        this.coreService.showSnackbar("Please select a company first.");
        return;
    }

    if (this.AllowNewDomain) {
      this.selectedSite = {
        companyID: Number(this.companyID)
      } as SocialAIContentSIteI;

      this.dialog.open(DialogComponent, {
        data: {
          content: this.siteFormTmpl,
          header: "Add Content Site",
          cls: "!bg-slate-600 !text-white",
        },
        width: "500px",
        maxWidth: "500px",
      });
    } else {
      this.coreService.showSnackbar(
        "You have exceed max number of domains allowed by your economic plan."
      );
    }
  }

  editSite(row: SocialAIContentSIteI): void {
    if (this.companyID === null) {
      this.coreService.showSnackbar("Company ID is not set. Cannot edit site.");
      return;
    }

    this.selectedSite = {
      ...row,
      id: row.ID || (row as any).id 
    } as SocialAIContentSIteI;


    if ('ID' in this.selectedSite) {
      delete (this.selectedSite as any).ID;
    }


    this.dialog.open(DialogComponent, {
      data: {
        content: this.siteFormTmpl,
        header: "Edit Site",
        cls: "!bg-slate-600 !text-white",
      },
      width: "500px",
      maxWidth: "500px",
    });
  }

  deleteSite(): void {
    const siteIdToDelete = this.selectedSite.ID || (this.selectedSite as any).ID;
    if (!siteIdToDelete) {
      this.coreService.showSnackbar("Site ID is missing for deletion.");
      return;
    }

    this.coreService
      .deleteData("deleteSite", String(siteIdToDelete))
      .pipe(
        catchError((error) => {
          console.error("Error deleting site:", error);
          this.coreService.showSnackbar("Error deleting site. Please try again.");
          return observableOf(null);
        })
      )
      .subscribe((result) => {
        if (result !== null) { 
          localStorage.removeItem("sites"); 
          this.coreService.showSnackbar("Site deleted successfully!");
          this.onSubmitClbk(); 
        }
      });
  }

  openDeleteSiteConfirm(row: SocialAIContentSIteI) {
    this.selectedSite = row; 
    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteSiteTmpl,
        header: "Delete Site?",
        cls: "bg-red-500 !text-white",
      },
    });
  }

  onStatusChange(element: SocialAIContentSIteI): void {
    const siteIdToUpdate = element.ID || (element as any).id;
    if (!siteIdToUpdate) {
      console.error("Site ID is missing for status update.");
      this.coreService.showSnackbar("Site ID is missing for status update.");
      return;
    }

    if (element.isApproved === 'Approved') {
      this.setApprovestatus(element);
    } else if (element.isApproved === 'Not Approved') {
      this.setNotApprovedStatus(element);
    }
  }

  setApprovestatus(row: SocialAIContentSIteI): void {
    const siteId = row.ID || (row as any).id;
    if (!siteId) {
      console.error("Site ID is missing for approval.");
      this.coreService.showSnackbar("Site ID is missing for approval.");
      return;
    }

    this.coreService.postData('approveSocialAiSite/approve', { ID: siteId }).subscribe({
      next: (data) => {
        this.coreService.showSnackbar("Site approved successfully!");
        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
        this.coreService.showSnackbar("Error approving site. Please try again.");
      }
    });
  }

  setNotApprovedStatus(row: SocialAIContentSIteI): void {
    const siteId = row.ID || (row as any).id;
    if (!siteId) {
      console.error("Site ID is missing for decline.");
      this.coreService.showSnackbar("Site ID is missing for decline.");
      return;
    }

    this.coreService.postData('approveSocialAiSite/decline', { ID: siteId }).subscribe({
      next: (data) => {
        this.coreService.showSnackbar("Site status set to Not Approved successfully!");
        this.onSubmitClbk();
      },
      error: (error) => {
        console.error("Error updating approval status:", error);
        this.coreService.showSnackbar("Error declining site. Please try again.");
      }
    });
  }

  onSubmitClbk(): void {
    this.dialog.closeAll();
    this.loadSites();
  }
}